import * as _ from "lodash";
import { call, put, select } from "redux-saga/effects";

import actions from "@/actions";
import type Light from "@/models/light";
import { Api, ConfigurationManager } from "@/utils";

import loadFloorSpaceCatalogs from "./load-floor-space-catalogs";

/**
 * Uncatalogs the currently selected lights by clearing their FloorSpaceCatalog association. The API deletes any
 * catalog record the unlink leaves unreferenced.
 *
 * @yields Selects current lights and dispatches the luminaire position update.
 */
function* uncatalogLights() {
    try {
        yield put(
            actions.window.openLoading("Uncataloging selected lights..."),
        );

        const getMode = (name: string) => (name === "view" ? "global" : "mode");

        const { lights, selectedLights } = yield select(({ map }) => ({
            selectedLights: map.general.selectedLights,
            lights: map.values[getMode(map.general.mode.name)].present.lights,
        }));

        const hasLightsToUncatalog = selectedLights.some((uniqueId: string) => {
            const light: Light = lights[uniqueId];
            return light?.options?.floorSpaceCatalog_ID || light?.id;
        });

        if (!hasLightsToUncatalog) {
            yield put(actions.window.closeLoading());

            yield put(
                actions.window.showMessage(
                    "No catalog associations found for the selected lights.",
                    "info",
                ),
            );

            return;
        }

        const lightsToUpdate = selectedLights
            .filter(
                (uniqueId: string) =>
                    lights[uniqueId]?.options?.floorSpaceCatalog_ID ||
                    lights[uniqueId]?.id,
            )
            .map((uniqueId: string) => ({
                ...lights[uniqueId].convertToAPI(),
                floorSpaceCatalog_ID: null,
                id: 0,
            }));

        yield call(Api.put, "v1/luminaire-positions/", lightsToUpdate);

        const updatedLights = _.cloneDeep(lights);

        selectedLights.forEach((uniqueId: string) => {
            const light: Light = updatedLights[uniqueId];

            if (light?.options?.floorSpaceCatalog_ID || light?.id) {
                light.options.floorSpaceCatalog_ID = null;
                light.id = 0;
            }
        });

        const { mode } = yield select(({ map }) => ({
            mode: getMode(map.general.mode.name),
        }));

        yield put(actions.map.values[mode].updateLightsSilent(updatedLights));

        const { floorSpaceId } = ConfigurationManager.values;

        yield call(loadFloorSpaceCatalogs, { floorSpaceId });

        yield put(actions.window.closeLoading());

        yield put(
            actions.window.showMessage(
                `Successfully uncataloged ${lightsToUpdate.length} light(s).`,
                "info",
            ),
        );
    } catch (err) {
        yield put(actions.window.closeLoading());

        yield put(
            actions.window.showMessage(
                "Failed to uncatalog lights. Please try again.",
                "error",
            ),
        );

        // eslint-disable-next-line no-console
        console.log("err on uncataloging lights:", err);
    }
}

export default uncatalogLights;
